import type { FileAsset } from "@nlc/shared-types";
import type { Path } from "react-hook-form";

import { SparklesIcon } from "lucide-react";
import { useState } from "react";

import type { MediaItem } from "@/components/media-preview-modal/mediaPreviewModal.types.ts";

import { MediaPreviewModal } from "@/components/media-preview-modal/index.tsx";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess.tsx";
import { useDownloadFile } from "@/features/my-files/hooks/useDownloadFile";
import { CollapsibleBox } from "@/features/request/edit/components/CollapsibleBox.tsx";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm.tsx";
import { useTemplateLock } from "@/features/request/edit/hooks/useTemplateLock.tsx";
import { mapAttachmentsToAssets } from "@/features/request/edit/utils/utils.ts";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

import type { RequestFormData } from "../assets-section/types.ts";

import { useGetAttachments } from "../../hooks/useGetAttachments.tsx";
import { AssetDownloadSection } from "../assets-section/components/AssetsDownloadButtons.tsx";
import { AssetsForm } from "../assets-section/components/AssetsForm.tsx";
import { isFileAsset } from "../assets-section/utils/uploadUtils.ts";

export function InspirationSection<TFormValues extends RequestFormData>() {
  const formMethods = useEditRequestFormContext();
  const { canAccess } = useRoleAccess();

  const { isLocked } = useTemplateLock();
  const isReadOnly =
    canAccess("edit-request.details.designer.view") || isLocked;

  return (
    <CollapsibleBox
      icon={<SparklesIcon className="text-white" size={16} />}
      iconClassName="bg-green-500"
      title="Inspiration"
    >
      {isReadOnly ? (
        <ReadOnlyInspirations />
      ) : (
        <AssetsForm
          field={"inspirations" as Path<TFormValues>}
          formMethods={formMethods}
          title="Examples for inspirations"
        />
      )}
    </CollapsibleBox>
  );
}

function ReadOnlyInspirations() {
  const { data: attachments } = useGetAttachments();
  const { example: inspirations } = mapAttachmentsToAssets(attachments || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>();
  const { mutate: downloadFile } = useDownloadFile();

  const fileInspirations = inspirations.filter(
    (element): element is FileAsset => isFileAsset(element),
  );

  const inspirationMediaItems: MediaItem[] = fileInspirations.map((asset) => ({
    extension: getExtensionFromFilename(asset.file.name),
    id: asset.id,
    name: asset.file.name,
    url: asset.awsDownloadUrl || "",
  }));

  const handleInspirationClick = (assetId: string) => {
    setSelectedAssetId(assetId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDownloadAsset = (item: MediaItem) => {
    if (item.id) {
      const asset = fileInspirations.find((a) => a.id === item.id);

      if (asset?.attachmentId) {
        downloadFile(asset.attachmentId);
      }
    }
  };

  return (
    <>
      <AssetDownloadSection
        assets={inspirations}
        emptyMessage="No inspirations available"
        onAssetClick={handleInspirationClick}
        title="Examples for inspirations"
      />

      {isModalOpen && (
        <MediaPreviewModal
          alt=""
          currentItemId={selectedAssetId}
          mediaItems={inspirationMediaItems}
          onClose={handleModalClose}
          onDownload={handleDownloadAsset}
        />
      )}
    </>
  );
}
